import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { InstanceHostSelection } from "../host_selection/Instance";
import { HostedMeetingFallbackSection } from "../meeting_details/HostedMeetingFallbackSection";
import { Meeting } from "../../../../model/meeting";
import { useBoundStore } from "../../../../store/store";
import { Host } from "../../../../model/host";
import { MeetingTypeInfoSelect } from "../MeetingTypeInfoSelect";
import { ApplyScope, ApplyScopeDialog } from "./ApplyScopeDialog";
import { MeetingHostSchedule } from "../../../../model/meeting_host_schedule";

interface Props {
  meeting: Meeting;
  open: boolean;
  onClose: () => void;
}

export const InstanceMetadataEditDialog: React.FC<Props> = ({
  meeting: originalMeeting,
  open,
  onClose,
}) => {
  const setInstanceMetadata = useBoundStore(
    (state) => state.setInstanceMetadata
  );
  const setRecurringMetadata = useBoundStore(
    (state) => state.setRecurringMetadata
  );
  const [currentMeeting, setCurrentMeeting] = useState(originalMeeting);
  const [scopePromptOpen, setScopePromptOpen] = useState(false);

  const recurringMeeting = originalMeeting.recurringMeeting;
  const isRecurring = recurringMeeting !== undefined;

  const applyChanges = React.useCallback(
    async (scope: ApplyScope) => {
      if (scope === ApplyScope.Recurring && recurringMeeting) {
        // "All events" — push form values up to the master. Per-day host
        // overrides on the series are kept; only `default` is updated when
        // the form has a host. Use the Edit Recurrence dialog for full
        // schedule editing.
        const existingSchedule =
          recurringMeeting.metadata.hostSchedule ?? ({} as MeetingHostSchedule);
        const nextSchedule: MeetingHostSchedule | undefined =
          currentMeeting.metadata.host
            ? { ...existingSchedule, default: currentMeeting.metadata.host }
            : recurringMeeting.metadata.hostSchedule;

        await setRecurringMetadata(recurringMeeting.id, {
          meetingType: currentMeeting.metadata.meetingType,
          memberVisible: currentMeeting.metadata.memberVisible,
          hostSchedule: nextSchedule,
        });
      } else {
        await setInstanceMetadata(currentMeeting.id, currentMeeting.metadata);
      }
      onClose();
    },
    [currentMeeting, recurringMeeting, setInstanceMetadata, setRecurringMetadata, onClose]
  );

  const handleSave = React.useCallback(async () => {
    if (isRecurring) {
      setScopePromptOpen(true);
      return;
    }
    await applyChanges(ApplyScope.Instance);
  }, [isRecurring, applyChanges]);

  const handleScopeChosen = React.useCallback(
    async (scope: ApplyScope) => {
      setScopePromptOpen(false);
      await applyChanges(scope);
    },
    [applyChanges]
  );

  return (
    <>
      <Dialog maxWidth="sm" fullWidth open={open} onClose={onClose}>
        <DialogTitle>
          <Stack gap={0.5}>
            {"Editing "}
            {currentMeeting.title}
            {isRecurring && (
              <Typography variant="caption">
                You're editing a single instance of a recurring meeting. On
                save, you'll be asked whether to apply changes to this event
                only or to the entire series.
              </Typography>
            )}
          </Stack>
        </DialogTitle>

        <DialogContent>
          <InstanceHostSelection
            value={currentMeeting.metadata.host}
            onChange={(value: Host) => {
              setCurrentMeeting({
                ...currentMeeting,
                metadata: { ...currentMeeting.metadata, host: value },
              });
            }}
          />

          <MeetingTypeInfoSelect
            currentMeetingTypeId={currentMeeting.metadata.meetingType}
            onMeetingTypeChange={(meetingTypeId: string) => {
              setCurrentMeeting({
                ...currentMeeting,
                metadata: { ...currentMeeting.metadata, meetingType: meetingTypeId },
              });
            }}
            formControlProps={{ fullWidth: true, margin: "normal" }}
            selectProps={{ label: "Meeting Type" }}
          />

          <FormControl fullWidth margin="normal">
            <FormControlLabel
              control={
                <Switch
                  checked={currentMeeting.metadata.memberVisible ?? false}
                  onChange={() =>
                    setCurrentMeeting({
                      ...currentMeeting,
                      metadata: {
                        ...currentMeeting.metadata,
                        memberVisible: !currentMeeting.metadata.memberVisible,
                      },
                    })
                  }
                />
              }
              label="Visible to Members"
            />
            <FormHelperText>
              When checked, members are able to see and subscribe to this meeting
            </FormHelperText>
          </FormControl>

          <HostedMeetingFallbackSection gcalEventId={currentMeeting.id} />
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSave} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <ApplyScopeDialog
        open={scopePromptOpen}
        onCancel={() => setScopePromptOpen(false)}
        onChoose={handleScopeChosen}
      />
    </>
  );
};
